import { useDarkMode } from "@/hooks/useDarkMode";
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import ShareButton from '@/components/ShareButton';
import { ArrowLeft, Sparkles, Users } from 'lucide-react';
export default function KeepsakeDetail({ user }) {
  const darkMode = useDarkMode();
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [qRes, rRes] = await Promise.all([
        fetch(`${api}/keepsakes/${id}`, { credentials: 'include' }),
        fetch(`${api}/keepsakes/${id}/responses`, { credentials: 'include' })
      ]);

      if (qRes.ok && rRes.ok) {
        setQuestionnaire(await qRes.json());
        setResponses(await rRes.json());
      }
    } catch (error) {
      logger.error('Failed to fetch data:', error);
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, id, navigate, fetchData]);

  const handleAnalyze = async () => {
    if (responses.length < 1) {
      toast.error('Need at least 1 response to analyze');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await fetch(`${api}/keepsakes/${id}/analyze`, {
        method: 'POST',
        credentials: 'include',
      });

      // Read the body exactly once with text() (safest method, survives
      // empty bodies and other fetch interceptors / in-app webviews that
      // may have already inspected the Response object).
      const status = response.status;
      const ok = response.ok;
      let bodyText = '';
      try { bodyText = await response.text(); } catch (_) { /* body already consumed */ }

      if (ok) {
        toast.success('Analysis complete!');
        navigate(`/keepsakes/${id}/results`);
        return;
      }

      let detail = `Analysis failed (status ${status})`;
      try {
        const parsed = JSON.parse(bodyText);
        if (parsed?.detail) detail = parsed.detail;
      } catch (_) { /* non-JSON body */ }
      throw new Error(detail);
    } catch (error) {
      toast.error(error.message);
      logger.error(error);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752]"></div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl mb-4" style={{ color: '#73766A' }}>Questionnaire not found</p>
          <Link to="/keepsakes">
            <Button>Back to Keepsakes</Button>
          </Link>
        </div>
      </div>
    );
  }

  const shareLink = `${window.location.origin}/gift-questionnaire/${questionnaire.share_id}`;

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back to the recipient's profile so the user doesn't lose
            context after sharing or analyzing. */}
        <button
          onClick={() => navigate(`/keepsakes?recipientName=${encodeURIComponent(questionnaire.recipient_name)}`)}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#9E6752' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {questionnaire.recipient_name}
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>{questionnaire.recipient_name}</h1>
          <p className="text-base" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>{questionnaire.occasion} • {questionnaire.relationship}</p>
        </div>

        {/* Share link card */}
        <div 
          className="rounded-xl p-4 sm:p-6 mb-6" 
          style={{ 
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.08)' 
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-base sm:text-lg font-bold mb-1" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Share with Friends & Family</h2>
              <p className="text-xs sm:text-sm" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>Send this link to people who know {questionnaire.recipient_name} well</p>
            </div>
            <div className="flex items-center gap-2" style={{ color: '#9E6752' }}>
              <Users className="w-5 h-5" />
              <span className="text-xl sm:text-2xl font-bold">{responses.length}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div 
              className="flex-1 px-4 py-3 rounded-lg"
              style={{
                background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : '#F9FAFB',
                border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#E5E7EB'}`
              }}
            >
              <p className="text-xs sm:text-sm truncate" style={{ color: darkMode.isDark ? '#FED7A5' : '#6B7280' }}>
                {window.location.origin}/gift-questionnaire/{questionnaire.share_id}
              </p>
            </div>
            <ShareButton
              shareUrl={`/gift-questionnaire/${questionnaire.share_id}`}
              title={`Help me find a gift for ${questionnaire.recipient_name}!`}
              description={`Share your thoughts about ${questionnaire.recipient_name} to help me pick the perfect ${questionnaire.occasion} gift.`}
            />
          </div>
        </div>

        {/* Questions preview */}
        <div 
          className="rounded-xl p-6 mb-6" 
          style={{ 
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.08)' 
          }}
        >
          <h2 className="text-lg font-bold mb-4" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Questions</h2>
          <div className="space-y-3">
            {questionnaire.questions.map((q, idx) => (
              <div key={`q-${idx}-${q.substring(0, 20)}`} className="flex gap-3">
                <span className="text-sm font-bold" style={{ color: '#9E6752' }}>{idx + 1}.</span>
                <p className="text-sm" style={{ color: darkMode.isDark ? '#E5E5E5' : '#534145' }}>{q}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Responses */}
        <div 
          className="rounded-xl p-6 mb-6" 
          style={{ 
            background: darkMode.isDark ? '#2D4354' : 'white',
            boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.08)' 
          }}
        >
          <h2 className="text-lg font-bold mb-4" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Responses ({responses.length})</h2>
          {responses.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>No responses yet. Share the link above to start collecting insights!</p>
          ) : (
            <div className="space-y-4">
              {responses.map((r, idx) => (
                <div 
                  key={r.id} 
                  className="p-4 rounded-lg" 
                  style={{ 
                    background: darkMode.isDark ? 'rgba(255, 255, 255, 0.05)' : '#F9FAFB',
                    border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.2)' : 'transparent'}`
                  }}
                >
                  <p className="font-semibold mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Response from {r.respondent_name}</p>
                  <p className="text-xs mb-3" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>{new Date(r.submitted_at).toLocaleDateString()}</p>
                  <div className="space-y-2">
                    {r.answers.slice(0, 3).map((answer, i) => (
                      <p key={`answer-${i}-${answer.substring(0, 15)}`} className="text-sm" style={{ color: darkMode.isDark ? '#E5E5E5' : '#534145' }}>• {answer.substring(0, 100)}{answer.length > 100 ? '...' : ''}</p>
                    ))}
                    {r.answers.length > 3 && (
                      <p className="text-xs" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>+ {r.answers.length - 3} more answers</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Analyze button */}
        {questionnaire.status !== 'completed' && (
          <div 
            className="rounded-xl p-6" 
            style={{ 
              background: darkMode.isDark ? '#2D4354' : 'white',
              boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.08)' 
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold mb-1" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>Ready for recommendations?</h2>
                <p className="text-sm" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>
                  {responses.length < 1
                    ? 'Collect at least one response to generate gift suggestions.'
                    : responses.length < 4
                      ? `You have ${responses.length} response${responses.length !== 1 ? 's' : ''}. The more responses, the sharper the suggestions, but you can analyze whenever you're ready.`
                      : 'Get personalized gift suggestions based on all responses.'}
                </p>
              </div>
              <Button
                onClick={handleAnalyze}
                disabled={responses.length < 1 || analyzing}
                style={{ background: '#9E6752', color: 'white' }}
                data-testid="analyze-responses-btn"
              >
                {analyzing ? (
                  'Analyzing...'
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyze Responses
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
